import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';
import { Trophy, Lightbulb, Eye } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import type { ChallengeData } from '../data/quiz-data';

const DIFFICULTY_STYLES: Record<string, string> = {
  facil: 'bg-green-100 text-green-700',
  medio: 'bg-yellow-100 text-yellow-700',
  avanzado: 'bg-red-100 text-red-700',
};

const DIFFICULTY_LABELS: Record<string, string> = {
  facil: 'Fácil',
  medio: 'Medio',
  avanzado: 'Avanzado',
};

interface ChallengeCardProps {
  challenge: ChallengeData;
  className?: string;
}

export function ChallengeCard({ challenge, className }: ChallengeCardProps) {
  const [showHint, setShowHint] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <Card className={cn('overflow-hidden', className)}>
      <div className="h-1 bg-gradient-to-r from-amber-400 to-orange-500" />
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-semibold">{challenge.title}</span>
          </div>
          <Badge className={cn('text-[10px]', DIFFICULTY_STYLES[challenge.difficulty])}>
            {DIFFICULTY_LABELS[challenge.difficulty]}
          </Badge>
        </div>

        {/* Question */}
        <p className="text-sm text-muted-foreground">{challenge.question}</p>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          {challenge.hint && !showHint && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setShowHint(true)}
            >
              <Lightbulb className="w-3 h-3" />
              Pista
            </Button>
          )}
          {!showAnswer && (
            <Button
              variant="default"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={() => setShowAnswer(true)}
            >
              <Eye className="w-3 h-3" />
              Revelar Respuesta
            </Button>
          )}
        </div>

        {/* Hint */}
        <AnimatePresence>
          {showHint && challenge.hint && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Lightbulb className="w-3 h-3 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-700">Pista</span>
                </div>
                <p className="text-xs text-amber-800">{challenge.hint}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Answer */}
        <AnimatePresence>
          {showAnswer && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Trophy className="w-3 h-3 text-green-600" />
                  <span className="text-xs font-semibold text-green-700">Respuesta</span>
                </div>
                <p className="text-xs text-green-800 whitespace-pre-wrap">{challenge.answer}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
